import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';
import type { UseUrlPaginationReturn, UseUrlStateReturnNumber, UseUrlStateReturnString } from '@/types/application/common/hooks/use-url-state';

export function useUrlState(key: string, defaultValue: string): UseUrlStateReturnString;
export function useUrlState(key: string, defaultValue: number): UseUrlStateReturnNumber;
export function useUrlState(key: string, defaultValue: string | number): UseUrlStateReturnString | UseUrlStateReturnNumber {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawValue = searchParams.get(key);
  
  // Always call useCallback hooks - React hooks must be called in the same order
  // Functional setSearchParams so consecutive updates from different hooks merge (no stale overwrite).
  const setValueNumber = useCallback((newValue: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(key, String(newValue));
      return params;
    }, { replace: true });
  }, [key, setSearchParams]);

  const setValueString = useCallback((newValue: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(key, newValue);
      return params;
    }, { replace: true });
  }, [key, setSearchParams]);
  
  if (typeof defaultValue === 'number') {
    const value = rawValue !== null ? parseInt(rawValue, 10) : defaultValue;
    return { value, setValue: setValueNumber };
  }

  const value = rawValue !== null ? rawValue : defaultValue;
  return { value, setValue: setValueString };
}

const ALLOWED_ROWS_PER_PAGE = [5, 10, 25] as const;

export function useUrlPagination(pageKey: string): UseUrlPaginationReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedPage = parseInt(searchParams.get(`${pageKey}_p`) || '0', 10);
  const page = Number.isFinite(parsedPage) && parsedPage >= 0 ? parsedPage : 0;

  const parsedRpp = parseInt(searchParams.get(`${pageKey}_r`) || '10', 10);
  const rowsPerPage = (ALLOWED_ROWS_PER_PAGE as readonly number[]).includes(parsedRpp) ? parsedRpp : 10;

  const setPage = useCallback((newPage: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(`${pageKey}_p`, String(newPage));
      return params;
    }, { replace: true });
  }, [pageKey, setSearchParams]);

  const setRowsPerPage = useCallback((newRows: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(`${pageKey}_r`, String(newRows));
      params.set(`${pageKey}_p`, '0');
      return params;
    }, { replace: true });
  }, [pageKey, setSearchParams]);

  return { page, rowsPerPage, setPage, setRowsPerPage };
}

export default useUrlState;

